import { useTranslation } from "react-i18next";
import { Heart } from "lucide-react";

type Props = {
    liked: boolean;
    count: number;
    onToggle: () => void;
    /** A like on its way to the server: one at a time, so they land in order. */
    pending?: boolean;
    /**
     * The author's own work: the count is theirs to see, the heart not theirs
     * to give.
     */
    readOnly?: boolean;
};

/** A heart and how many therapists gave one: meditations and music alike. */
export default function LikeHeart({ liked, count, onToggle, pending = false, readOnly = false }: Props) {
    const { t } = useTranslation();
    const countLabel = t("meditation.likes.count", { count });

    if (readOnly) {
        return (
            <span
                className="flex min-h-9 items-center gap-1 px-2.5 text-xs font-medium text-content-tertiary"
                title={countLabel}
            >
                <Heart size={14} aria-hidden="true" />
                <span className="tabular-nums" aria-hidden="true">
                    {count}
                </span>
                <span className="sr-only">{countLabel}</span>
            </span>
        );
    }

    const label = t(liked ? "meditation.likes.unlike" : "meditation.likes.like");

    return (
        <button
            type="button"
            aria-pressed={liked}
            aria-label={`${label} · ${countLabel}`}
            title={label}
            onClick={onToggle}
            disabled={pending}
            className={`flex min-h-9 items-center gap-1 rounded-full px-2.5 text-xs font-medium transition-colors disabled:cursor-wait ${
                liked
                    ? "bg-accent-tint text-accent-on-tint"
                    : "text-content-tertiary hover:bg-surface-sunken hover:text-content-secondary"
            }`}
        >
            <Heart size={14} aria-hidden="true" className={liked ? "fill-current" : undefined} />
            <span className="tabular-nums">{count}</span>
        </button>
    );
}
